import { useCallback } from "react";
import TwoColumnLayout from "@/components/Layout/TwoColumnLayout";
import { CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ImportDialog } from "@/components/ui/import-dialog";
import { Limit } from "@/components/Limit";
import { DateRange } from "@/components/DateRange";
import { exportInfluencerResultsToCSV } from "@/features/keyword-monitoring/utils/csvExport";
import InfluencersTable from "../components/Influencer/InfluencerTable";
import { useInfluencerContainer } from "../hooks/useInfluencerContainer";
export default function InfluencerMonitoring() {
  const {
    state,
    influencerData,
    matchedInfluencers,
    isFetching,
    showResults,
    hasLaunched,
    isEmptyResults,
    responseJSON,
    apiEndpoint,
    showLinks,
    selectedToken,
    effectiveToken,
    isLaunchDisabled,
    isApiTokenLoading,
    tokenSelectedPlatforms,
    availableTokens,
    tokensLoading,
    handleShowResults,
    handleLaunch,
    handleCopyCode,
    handleTokenSelect,
    handlePanelResize,
    handleUpload,
    setShowLinks,
    refetch,
    linkValidation,
    monitoringType,
  } = useInfluencerContainer();

  const handleExportJSON = useCallback(() => {
    const blob = new Blob([JSON.stringify(matchedInfluencers ?? [], null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "influencers.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [matchedInfluencers]);

  const handleExportCSV = useCallback(() => {
    exportInfluencerResultsToCSV(matchedInfluencers, "influencer_results.csv");
  }, [matchedInfluencers]);

  return (
    <TwoColumnLayout
      data={matchedInfluencers}
      isLoading={isFetching}
      isEmptyResults={isEmptyResults}
      responseJSON={responseJSON}
      apiEndpoint={apiEndpoint}
      apiToken={effectiveToken}
      isApiTokenLoading={isApiTokenLoading}
      resultsTitle="Results"
      resultsDescription={
        matchedInfluencers?.length
          ? `${matchedInfluencers.length} influencer(s) matched`
          : "No results yet"
      }
      showResults={showResults}
      onShowResults={handleShowResults}
      onLaunch={handleLaunch}
      onCopyCode={handleCopyCode}
      handleExportCSV={handleExportCSV}
      handleExportJSON={handleExportJSON}
      floatingButtonCondition={hasLaunched}
      leftPanelMinSize={50}
      rightPanelMinSize={30}
      onPanelResize={handlePanelResize}
      showTokenDropdown={true}
      onTokenSelect={handleTokenSelect}
      monitoringType={monitoringType}
      selectedToken={selectedToken}
      availableTokens={availableTokens}
      tokensLoading={tokensLoading}
    >
      <div className="flex h-full flex-col gap-4">
        <div className="flex w-full flex-col items-start justify-between gap-5">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-[28px] !leading-[36px] !-tracking-wide text-neutral-950">
              Influencer Monitoring
            </CardTitle>
          </div>

          <div className="flex w-full flex-row flex-wrap items-end justify-between gap-4">
            <div className="scrollbar-thin flex flex-row gap-4">
              <Limit
                limit={state.limit}
                setLimit={state.setLimit}
                useHeader={false}
                customWidth="90px"
              />
              <DateRange
                dateRange={state.dateRange}
                setDateRange={(value) => state.setDateRange(value as any)}
                useHeader={false}
                customWidth="175px"
              />
            </div>
            <div className="flex flex-row flex-wrap items-center gap-[10px]">
              <div className="flex items-end gap-2">
                <Checkbox
                  id="show-links"
                  checked={showLinks}
                  onCheckedChange={(checked) => setShowLinks(!!checked)}
                />
                <label
                  htmlFor="show-links"
                  className="cursor-pointer text-sm font-semibold text-neutral-800"
                >
                  View Links
                </label>
              </div>

              <ImportDialog
                trigger={
                  <Button
                    variant="outline"
                    icon
                    className="text-muted flex h-[48px] w-auto flex-row gap-1 border-neutral-400 p-3"
                  >
                    <i className="ri-file-excel-2-line text-[16px]"></i> Import
                    Sheet
                  </Button>
                }
                onUpload={handleUpload}
              />
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-hidden rounded-[12px] bg-white">
            <InfluencersTable
              data={influencerData}
              onTriggerMatch={refetch}
              enabledPlatforms={tokenSelectedPlatforms}
              isApiTokenLoading={isApiTokenLoading}
              invalidLinks={linkValidation.invalidLinks}
              duplicateLinks={linkValidation.duplicateLinks}
            />
          </div>

          <div className="flex-shrink-0 pt-4">
            <Button
              variant="default"
              onClick={handleLaunch}
              disabled={isLaunchDisabled}
              className="h-[48px] w-[220px]"
            >
              {effectiveToken ? (
                <>
                  <i className="ri-arrow-right-s-fill text-[16px]"></i> Launch
                </>
              ) : (
                "Missing API Token"
              )}
            </Button>
          </div>
        </div>
      </div>
    </TwoColumnLayout>
  );
}
